import { assign, createMachine, fromPromise } from 'xstate'

import Api from '@/api/_legacy'
import { ApiAddBusinessUnitToClient, ApiBusinessUnit } from '@/api/_legacy/generated-api'

import { setErrorsAction } from '../common/set-errors-action'
import { Mutation } from '../types'

const TENANT_ID = 'absa'

export type EditBusinessUnitState = {
  businessUnit: ApiBusinessUnit
  clientId: string
  errorMessages: string[]
  errors: { [key: string]: string }
  isAdd: boolean
  isSaveEnabled: boolean
  isSaveSuccess: boolean
  onSaveSuccess: () => void
}

type InitEditEvent = {
  businessUnit: ApiBusinessUnit
  clientId: string
  onSaveSuccess: () => void
  type: 'INIT_EDIT'
}

type InitAddEvent = {
  clientId: string
  onSaveSuccess: () => void
  type: 'INIT_ADD'
}

type MutateEvent = {
  mutation: Mutation<ApiBusinessUnit>
  type: 'MUTATE'
}

type SaveCreateEvent = {
  businessUnit: ApiBusinessUnit
  type: 'SAVE_CREATE'
}

type SaveUpdateEvent = {
  businessUnit: ApiBusinessUnit
  type: 'SAVE_UPDATE'
}

export type EditBusinessUnitEvent =
  | InitAddEvent
  | InitEditEvent
  | MutateEvent
  | SaveCreateEvent
  | SaveUpdateEvent

export const editBusinessUnitMachine = createMachine(
  {
    types: {} as {
      context: EditBusinessUnitState
      events: EditBusinessUnitEvent
    },
    id: 'edit-business-unit',
    initial: 'idle',
    context: {
      isAdd: true,
      clientId: '',
      businessUnit: {
        businessUnitId: '',
        businessUnitName: '',
        competency: 'Entry',
        disallowedStrategies: []
      },
      isSaveSuccess: false,
      isSaveEnabled: false,
      errors: {},
      errorMessages: [],
      onSaveSuccess: () => true
    },
    states: {
      idle: {
        on: {
          INIT_EDIT: {
            actions: ['setBusinessUnit', 'setClientId', 'setEdit', 'setOnBusinessUnitSaved'],
            target: 'businessUnitLoaded'
          },
          INIT_ADD: {
            target: 'businessUnitLoaded',
            actions: ['setAdd', 'setClientId', 'setOnBusinessUnitSaved']
          }
        }
      },
      businessUnitLoaded: {
        on: {
          MUTATE: {
            actions: ['mutate', 'checkIfCanEnableSave'],
            target: 'businessUnitLoaded'
          },
          SAVE_CREATE: 'creating',
          SAVE_UPDATE: 'updating',
          INIT_EDIT: {
            actions: ['setBusinessUnit', 'setEdit'],
            target: 'businessUnitLoaded'
          }
        }
      },
      creating: {
        invoke: {
          id: 'createBusinessUnit',
          src: 'createBusinessUnit',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            actions: ['clearErrors', 'setSuccess', 'notifySaveSuccess'],
            target: 'businessUnitLoaded'
          },
          onError: {
            actions: ['setError'],
            target: 'businessUnitLoaded'
          }
        }
      },
      updating: {
        invoke: {
          id: 'updateBusinessUnit',
          src: 'updateBusinessUnit',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            actions: ['clearErrors', 'setSuccess', 'notifySaveSuccess'],
            target: 'businessUnitLoaded'
          },
          onError: {
            actions: ['setError'],
            target: 'businessUnitLoaded'
          }
        }
      }
    }
  },
  {
    actors: {
      createBusinessUnit: fromPromise(async ({ input }: any) => {
        const { context, event } = input
        const businessUnit = event.businessUnit as ApiBusinessUnit

        if (businessUnit.businessUnitName.length < 1) {
          throw {
            type: 'validation',
            errors: {
              businessUnitName: 'Please enter a valid business unit name'
            }
          }
        }

        const businessUnitToSave: ApiAddBusinessUnitToClient = {
          businessUnitName: businessUnit.businessUnitName,
          competency: businessUnit.competency,
          disallowedStrategies: businessUnit.disallowedStrategies
        }

        const { clientId } = context

        const response = await Api.tenant.businessUnitsCreate(
          clientId,
          TENANT_ID,
          businessUnitToSave
        )

        return response.data
      }),
      updateBusinessUnit: fromPromise(async ({ input }: any) => {
        const { context, event } = input
        const businessUnit = event.businessUnit as ApiBusinessUnit

        if (businessUnit.businessUnitName.length < 1) {
          throw {
            type: 'validation',
            errors: {
              businessUnitName: 'Please enter a valid business unit name'
            }
          }
        }

        const businessUnitToSave: ApiBusinessUnit = {
          businessUnitId: businessUnit.businessUnitId,
          businessUnitName: businessUnit.businessUnitName,
          competency: businessUnit.competency,
          disallowedStrategies: businessUnit.disallowedStrategies
        }

        const { clientId } = context

        const response = await Api.tenant.businessUnitsUpdate(
          clientId,
          TENANT_ID,
          businessUnitToSave
        )

        return response.data
      })
    },
    actions: {
      setBusinessUnit: assign(({ event }: any) => ({
        businessUnit: event.businessUnit
      })),
      setClientId: assign(({ event }: any) => ({
        clientId: event.clientId
      })),
      setOnBusinessUnitSaved: assign(({ event }: any) => ({
        onSaveSuccess: event.onSaveSuccess
      })),
      notifySaveSuccess: assign(({ context }) => {
        if (context.onSaveSuccess) {
          context.onSaveSuccess()
        }

        return {}
      }),
      setAdd: assign(() => ({
        isAdd: true
      })),
      setEdit: assign(() => ({
        isAdd: false
      })),
      mutate: assign(({ context, event }: any) => {
        const businessUnit = { ...context.businessUnit }
        event.mutation(businessUnit)

        return {
          businessUnit
        }
      }),
      checkIfCanEnableSave: assign(({ context }) => {
        const isSaveEnabled = context.businessUnit.businessUnitName !== ''

        return {
          isSaveEnabled
        }
      }),
      setError: assign(({ context, event }: any) => setErrorsAction(context, event)),
      clearErrors: assign(() => ({
        errors: {}
      })),
      setSuccess: assign(() => ({
        isSaveSuccess: true
      }))
    }
  }
)
